import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Button, ButtonGroup, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { add, format, lastDayOfMonth as lastDayOfMonthFn, sub } from 'date-fns';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { LoadingStatuses } from '@/constants/constants';
import { FocusCalendarViews, getCalendarStats, setView } from '@/redux/slices/focusCalendarSlice';
import { applyFilter } from '@/redux/slices/dataTableSlice';
import { getMonthsFirstDay, getMostRecentSunday, getUpcomingSaturday, parseStartDateQueryParam, CALENDAR_START_DATE_FORMAT } from '@/_utils/date';
import { useQuery } from '@/components/_hooks/useQuery';
import { useSetQueryParam } from '@/components/_hooks/useSetQueryParam';
import { FilterProps } from '@/components/_common/Filters/Filter';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectDataTableFilters } from '@/redux/selectors/dataTableSelectors';
import CalendarButton from '@/components/Focus/common/CalendarButton';
import CalendarFilterMultiSelect from '@/components/_common/Filters/CalendarFilterMultiSelect';
import SearchBar from '../SearchBar/SearchBar';
import { NewFilterTypesMap } from './FilterDictionary';
import { selectAvailDetailsById, selectSelectedAvailId } from '@/redux/selectors/focusCalendarSelectors';
import { RefreshTable } from '../Table';

interface Props {
  filters: FilterProps[];
}

const CalendarFilters: React.FC<Props> = ({ filters }) => {
  const dispatch = useAppDispatch();
  const query = useQuery();
  const calendarViewParam = query.get('calendarView') as FocusCalendarViews | null;
  const prevCalendarViewParam = useRef<FocusCalendarViews | null>(null);
  const calendarStartDateParam = query.get('calendarStartDate');
  const setQueryParam = useSetQueryParam();
  const platformFilter = filters.find((f) => f.id === 'availed_platform_filter');
  const appliedFilters = useAppSelector(selectDataTableFilters);
  const platforms: string[] = appliedFilters.availed_platform_filter || [];
  const searchFilter = filters.find((filter) => filter.type === NewFilterTypesMap.SEARCH);
  const selectedAvailId = useAppSelector(selectSelectedAvailId);
  const availDetails = useAppSelector((state) => selectAvailDetailsById(state, selectedAvailId || 0));

  // This hook synchronizes the `view` value stored in the redux with the `view` query param
  useEffect(() => {
    if (calendarViewParam) {
      dispatch(setView(calendarViewParam));
    }
  }, [calendarViewParam, setQueryParam]);

  // This hook sets `view` and `calendarStartDate` query params if these params don't exist in the query string.
  // This happens when the users click on the "Releases" link in the navigation (even if they are already on that page).
  useEffect(() => {
    let view = calendarViewParam;

    if (!view) {
      view = 'month';
      setQueryParam({ calendarView: view }, true);
    }

    if (!calendarStartDateParam) {
      // If we have avail opened in the side panel, we want to set a new `calendarStartDate` query param to the
      // `term_start` of opened avail, to make sure this avail will be included in the calendar view.
      const selectedAvailTermStart =
        selectedAvailId && availDetails?.loadingStatus === LoadingStatuses.fulfilled ? new Date(availDetails.data.term_start) : null;
      if (view === 'week') {
        setQueryParam({ calendarStartDate: format(selectedAvailTermStart ?? getMostRecentSunday(), CALENDAR_START_DATE_FORMAT) }, true);
      } else if (view === 'month') {
        setQueryParam({ calendarStartDate: format(selectedAvailTermStart ?? getMonthsFirstDay(), CALENDAR_START_DATE_FORMAT) }, true);
      }
    }
  }, [calendarViewParam, setQueryParam]);

  // The purpose of this hook is to update filters (by dispatching applyFilter({...})) when the `view` or
  // `calendarStartDate` query param change.
  useEffect(() => {
    if (!calendarStartDateParam) {
      return;
    }

    let startDate: Date;
    let endDate: Date;

    const startDateParamParsed = parseStartDateQueryParam(calendarStartDateParam);

    if (calendarViewParam === 'week') {
      startDate = getMostRecentSunday(startDateParamParsed);
      endDate = add(startDate, { days: 6 });
    } else if (calendarViewParam === 'month') {
      const firstDayOfMonth = getMonthsFirstDay(startDateParamParsed);
      const lastDayOfMonth = lastDayOfMonthFn(startDateParamParsed);
      startDate = getMostRecentSunday(firstDayOfMonth);
      endDate = getUpcomingSaturday(lastDayOfMonth);
    } else if (calendarViewParam === 'day') {
      startDate = startDateParamParsed;
      endDate = new Date(startDate);
    } else {
      return;
    }

    endDate.setHours(23, 59, 59, 999);

    const viewHasChanged = calendarViewParam !== prevCalendarViewParam.current;
    if (viewHasChanged) {
      const appliedFilterStartDate = appliedFilters.term_start.term_start__gte ? new Date(appliedFilters.term_start.term_start__gte) : null;
      const appliedFilterEndDate = appliedFilters.term_start.term_start__lte ? new Date(appliedFilters.term_start.term_start__lte) : null;
      if (selectedAvailId && appliedFilterStartDate && appliedFilterEndDate && availDetails?.loadingStatus === LoadingStatuses.fulfilled) {
        const selectedAvailTermStart = new Date(availDetails.data.term_start);

        /**
         * On view (month/week/day) change, the visible date range shifts. If avail is open in the side panel, ensure it
         * stays in the calendar view if it was there when the view change occurred.
         * If `selectedAvailTermStart` falls outside the new [startDate, endDate], update the `calendarStartDate` query param
         * (to jump to this date) and exit. The effect will rerun with the new param and apply the correct range.
         */
        if (
          appliedFilterStartDate < selectedAvailTermStart &&
          selectedAvailTermStart < appliedFilterEndDate &&
          (startDate > selectedAvailTermStart || endDate < selectedAvailTermStart)
        ) {
          setQueryParam({ calendarStartDate: format(selectedAvailTermStart, CALENDAR_START_DATE_FORMAT) }, true);
          return;
        }
      }
      prevCalendarViewParam.current = calendarViewParam;
    }

    dispatch(
      applyFilter({
        type: 'term_start',
        selections: {
          term_start__gte: startDate.toISOString(),
          term_start__lte: endDate.toISOString()
        }
      })
    );
  }, [calendarStartDateParam, calendarViewParam]);

  const handleTodayButtonClick = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setQueryParam({ calendarStartDate: format(today, CALENDAR_START_DATE_FORMAT) }, true);
  };

  const handleViewToggle = (_: React.MouseEvent<HTMLElement>, newView: FocusCalendarViews | null) => {
    if (!newView) return;
    setQueryParam({ calendarView: newView });
  };

  const handleArrowClick = (direction: 'left' | 'right') => {
    if (!calendarStartDateParam) return;

    const changeFn = direction === 'left' ? sub : add;
    const date = parseStartDateQueryParam(calendarStartDateParam);
    let newCalendarStartDate: Date;

    if (calendarViewParam === 'day') {
      newCalendarStartDate = changeFn(date, { days: 1 });
    } else if (calendarViewParam === 'week') {
      newCalendarStartDate = changeFn(date, { days: 7 });
    } else if (calendarViewParam === 'month') {
      const newDate = changeFn(date, { months: 1 });
      newDate.setDate(1);
      newCalendarStartDate = newDate;
    } else {
      return;
    }

    setQueryParam({ calendarStartDate: format(newCalendarStartDate, CALENDAR_START_DATE_FORMAT) });
  };

  return (
    <ButtonsWrapper>
      <CalendarButton value="today" onClick={handleTodayButtonClick}>
        <PolarisIcon icon="calendar" style={{ marginRight: 12 }} />
        Today
      </CalendarButton>
      <ToggleButtonGroupStyled value={calendarViewParam} exclusive onChange={handleViewToggle} aria-label="Calendar View Selection">
        <ToggleButtonStyled value="month">Month</ToggleButtonStyled>
        <ToggleButtonStyled value="week">Week</ToggleButtonStyled>
        <ToggleButtonStyled value="day">Day</ToggleButtonStyled>
      </ToggleButtonGroupStyled>
      <PrevNextButtonGroup>
        <PrevNextButton onClick={() => handleArrowClick('left')}>
          <PolarisIcon icon="left" width={28} height={28} color="#A3A8BA" />
        </PrevNextButton>
        <PrevNextButton onClick={() => handleArrowClick('right')}>
          <PolarisIcon icon="right" width={28} height={28} color="#A3A8BA" />
        </PrevNextButton>
      </PrevNextButtonGroup>

      {platformFilter && (
        <CalendarFilterMultiSelect label="Platform" type="availed_platform_filter" filter={platformFilter} selectedItems={platforms} />
      )}
      <SearchContainer>
        <RefreshTable onRefresh={() => dispatch(getCalendarStats({ appliedFilters }))} />
        {searchFilter && <SearchBar role={searchFilter.role} />}
      </SearchContainer>
    </ButtonsWrapper>
  );
};

export default CalendarFilters;

const SearchContainer = styled.div`
  display: flex;
  margin-left: auto;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(2)};
  border-top-left-radius: ${({ theme }) => `${theme.shape.borderRadius * 2.4}px`};
  border-top-right-radius: ${({ theme }) => `${theme.shape.borderRadius * 2.4}px`};
  background-color: ${({ theme }) => theme.customOverrides.TitleBar.background};
`;

const ToggleButtonGroupStyled = styled(ToggleButtonGroup)`
  padding: ${({ theme }) => theme.spacing(0.125)};
  gap: ${({ theme }) => theme.spacing(0.25)};
  background-color: ${({ theme }) => theme.palette.background.default};
`;

const ToggleButtonStyled = styled(ToggleButton)`
  border: 0;
  padding: ${({ theme }) => theme.spacing(0.75, 2)};
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.69rem;
  text-transform: none;
  background-color: transparent;
  color: ${({ theme }) => theme.customOverrides.Calendar.textTertiary};

  &.Mui-selected {
    background-color: ${({ theme }) => theme.customOverrides.Calendar.background};

    color: ${({ theme }) => (theme.palette.mode === 'dark' ? theme.palette.primary.main : theme.palette.primary.dark)};
  }

  &.MuiToggleButtonGroup-firstButton,
  &.MuiToggleButtonGroup-middleButton,
  &.MuiToggleButtonGroup-lastButton {
    margin: 0;
    border: 0;
    border-radius: ${({ theme }) => `${theme.shape.borderRadius}px;`};
  }
`;

const PrevNextButtonGroup = styled(ButtonGroup)`
  gap: ${({ theme }) => theme.spacing(0.25)};
`;

const PrevNextButton = styled(Button)`
  min-width: auto !important;
  border: 0;
  padding: ${({ theme }) => theme.spacing(0.5, 1)};
  background-color: ${({ theme }) => theme.customOverrides.Calendar.background};

  &:hover {
    border: 0;
    background-color: ${({ theme }) => theme.customOverrides.Calendar.hover};
  }
`;
